import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, Label, TextInput, Textarea } from 'flowbite-react';
import * as wasm from 'sched';
import { v4 as uuidv4 } from 'uuid';

// Define the Zod schema for native task
const NativeTaskSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  projectId: z.string().nullable(),
  complete: z.boolean().default(false),
  tags: z.array(z.string()).default([]),
});

// Define the form values type based on the schema
type NativeTaskFormValues = z.infer<typeof NativeTaskSchema>;

interface NativeTaskFormProps {
  onSubmit: (command: wasm.DatabaseCommand) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
  projects?: Array<{ id: string; name: string }>;
}

const NativeTaskForm: React.FC<NativeTaskFormProps> = ({ 
  onSubmit,
  onClose,
  isOpen,
  projects = []
}) => {
  
  const initialValues: NativeTaskFormValues = {
    title: '',
    description: '',
    projectId: null,
    complete: false,
    tags: [],
  };

  const handleSubmit = async (values: NativeTaskFormValues) => {
    // Generate a new ID for the task
    const taskId = uuidv4();
    
    // Create a NativeTask object
    const nativeTask: wasm.NativeTask = {
      title: values.title,
      description: values.description || null,
      estimates: [],
      project_id: values.projectId,
      tags: values.tags,
      complete: values.complete,
    };

    // Create the Task object (which wraps NativeTask)
    const task: wasm.Task = {
      Native: nativeTask
    };

    // Create the TaskGraphNodeData object
    const taskGraphNodeData: wasm.TaskGraphNodeData = {
      type: "Task",
      value: task
    };

    // Create the command
    const command: wasm.DatabaseCommand = {
      type: "CreateItem",
      id: taskId,
      item: taskGraphNodeData
    };

    await onSubmit(command);
    onClose();
  };

  // Function to handle tag input
  const handleTagInput = (
    e: React.KeyboardEvent<HTMLInputElement>,
    values: NativeTaskFormValues,
    setFieldValue: (field: string, value: any) => void
  ) => {
    if (e.key === 'Enter' && e.currentTarget.value.trim()) {
      e.preventDefault();
      const newTag = e.currentTarget.value.trim();
      if (!values.tags.includes(newTag)) {
        setFieldValue('tags', [...values.tags, newTag]);
      }
      e.currentTarget.value = '';
    }
  };

  // Function to remove a tag
  const removeTag = (
    tagToRemove: string,
    values: NativeTaskFormValues,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue(
      'tags',
      values.tags.filter(tag => tag !== tagToRemove)
    );
  };

  return (
    <div className={`${isOpen ? 'block' : 'hidden'} fixed inset-0 z-50 overflow-y-auto overflow-x-hidden flex justify-center items-center bg-cm-bg-deep/70`}>
      <div className="relative p-4 w-full max-w-4xl max-h-full">
        <div className="relative bg-cm-bg-surface rounded-lg shadow">
          {/* Header */}
          <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t">
            <h3 className="text-xl font-medium text-cm-text-primary">
              Create New Task
            </h3>
            <button 
              type="button" 
              className="text-cm-text-muted bg-transparent hover:bg-cm-border-subtle hover:text-cm-text-primary rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
              onClick={onClose}
            >
              <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>
          
          {/* Body */}
          <div className="p-4 md:p-5 space-y-4">
        <Formik
          initialValues={initialValues}
          validationSchema={toFormikValidationSchema(NativeTaskSchema)}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched, values, setFieldValue }) => (
            <Form className="space-y-4">
              <div>
                <Label htmlFor="title" className="mb-2 block">Title</Label>
                <Field
                  as={TextInput}
                  id="title"
                  name="title"
                  autoFocus
                  type="text"
                  placeholder="Task title"
                  color={errors.title && touched.title ? 'failure' : undefined}
                />
                <ErrorMessage name="title" component="p" className="text-red-500 text-sm mt-1" />
              </div>
              
              <div>
                <Label htmlFor="description" className="mb-2 block">Description</Label>
                <Field
                  as={Textarea}
                  id="description"
                  name="description"
                  placeholder="Task description (optional)"
                  rows={4}
                />
              </div>
              
              {projects.length > 0 && (
                <div>
                  <Label htmlFor="projectId" className="mb-2 block">Project</Label>
                  <Field
                    as="select"
                    id="projectId"
                    name="projectId"
                    className="bg-cm-bg-elevated border border-cm-border-subtle text-cm-text-primary text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:placeholder-gray-400 dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  >
                    <option value="">None</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </Field>
                </div>
              )}
              
              <div>
                <Label htmlFor="tags" className="mb-2 block">Tags</Label>
                <div className="mb-2 flex flex-wrap gap-2">
                  {values.tags.map(tag => (
                    <span
                      key={tag}
                      className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded dark:bg-blue-900 flex items-center"
                    >
                      {tag}
                      <button
                        type="button"
                        className="ml-1.5 text-blue-800 hover:text-blue-900"
                        onClick={() => removeTag(tag, values, setFieldValue)}
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder="Type a tag and press Enter"
                  className="bg-cm-bg-elevated border border-cm-border-subtle text-cm-text-primary text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:placeholder-gray-400 dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  onKeyDown={(e) => handleTagInput(e, values, setFieldValue)}
                />
              </div>
              
              <div className="flex items-center">
                <Field
                  type="checkbox"
                  id="complete"
                  name="complete"
                  className="w-4 h-4 text-blue-600 bg-cm-bg-hover border-cm-border-subtle rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2"
                />
                <Label htmlFor="complete" className="ml-2">Mark as completed</Label>
              </div>
              
              <div className="flex justify-end space-x-2 pt-4">
                <Button color="light" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  Create Task
                </Button>
              </div>
            </Form>
          )}
        </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NativeTaskForm;
